import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addEdge,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type Node,
} from "@xyflow/react";
import {
  Alert,
  Box,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppTextField } from "../../../components/form";
import { PageCommandBar } from "../../../components/layout";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { prepService } from "../../../services/prepService";
import type {
  PrepChainPreviewResponse,
  PrepGraphEdge,
  PrepGraphNode,
  PrepJobGraph,
} from "../../../types/prep";
import PrepAnalysisPanel from "../components/PrepAnalysisPanel";
import PrepExpansionRunDialog from "../components/PrepExpansionRunDialog";
import PrepFlowCanvas from "../components/PrepFlowCanvas";
import PrepDesignToolbar from "../components/PrepDesignToolbar";
import PrepConfigDrawer, { type PrepDrawerSelection } from "../components/drawers/PrepConfigDrawer";
import PrepChainPreviewDrawer from "../components/drawers/PrepChainPreviewDrawer";
import { usePrepJobRun } from "../hooks/usePrepJobRun";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import { autoLayoutNodes } from "../utils/autoLayout";
import {
  countIncompleteEdges,
  flowToGraph,
  graphToFlow,
  newDslNode,
  newAdaptorNode,
  newOpenApiNode,
  isPrepSourceType,
  outputTargetIndex,
  type PrepFlowNodeData,
} from "../utils/graphAdapter";

/** "Prep Job 2026-10-09 14:32": readable, and distinct enough to tell new jobs apart in the list. */
export function defaultPrepJobName(label: string, now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  return `${label} ${stamp}`;
}

export default function PrepJobDesignPage() {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = id === "new";
  const [jobId, setJobId] = useState<string | null>(isNew ? null : id ?? null);
  const [jobName, setJobName] = useState("");
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<PrepFlowNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selection, setSelection] = useState<PrepDrawerSelection | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysisText, setAnalysisText] = useState("");
  const [analysisPanelOpen, setAnalysisPanelOpen] = useState(false);
  const [staleNodeIds, setStaleNodeIds] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [lastValidatedAt, setLastValidatedAt] = useState<string | null>(null);
  const [chainPreviewOpen, setChainPreviewOpen] = useState(false);
  const [chainPreview, setChainPreview] = useState<PrepChainPreviewResponse | null>(null);
  const {
    running,
    runJob,
    expansionDialogOpen,
    expansionEstimate,
    confirmExpansionRun,
    cancelExpansionRun,
  } = usePrepJobRun();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const graphRef = useRef<PrepJobGraph | null>(null);
  const drawerDraftApplierRef = useRef<((graph: PrepJobGraph) => PrepJobGraph) | null>(null);
  const loadedJobId = useRef<string | null>(null);

  const applyFlow = useCallback(
    (g: PrepJobGraph, stale: string[] = []) => {
      const flow = graphToFlow(g, stale);
      setNodes(flow.nodes);
      setEdges(flow.edges);
      graphRef.current = g;
    },
    [setNodes, setEdges]
  );

  const loadJob = useCallback(
    async (loadId: string) => {
      const job = await prepService.get(loadId);
      setJobName(job.name ?? "");
      setAnalysisText(job.analysisText ?? "");
      graphRef.current = job.graph;
      let stale: string[] = [];
      try {
        const validation = await prepService.validate(loadId);
        stale = validation.staleNodeIds ?? [];
        setStaleNodeIds(stale);
        setValidationErrors(validation.valid ? [] : validation.errors);
      } catch {
        setStaleNodeIds([]);
        setValidationErrors([]);
      }
      applyFlow(job.graph, stale);
    },
    [applyFlow]
  );

  // Fix: only load when route id changes — avoid wiping local edits on validate/stale updates
  useEffect(() => {
    if (!isNew && id && loadedJobId.current !== id) {
      loadedJobId.current = id;
      loadJob(id).catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
      return;
    }
    if (isNew && loadedJobId.current !== "new") {
      loadedJobId.current = "new";
      setJobName(defaultPrepJobName(t("prep.design.defaultName")));
      const outputId = crypto.randomUUID();
      const initial: PrepJobGraph = {
        nodes: [
          {
            id: outputId,
            type: "OUTPUT",
            label: "Output",
            enabled: true,
            indexApprovalStatus: "PENDING",
            refreshMode: "FULL",
            piiMaskEnabled: true,
            skipFailedRecords: false,
            environment: "STAGING",
          },
        ],
        edges: [],
        positions: { [outputId]: { x: 480, y: 120 } },
      };
      graphRef.current = initial;
      applyFlow(initial);
    }
  }, [id, isNew, loadJob, applyFlow, t]);

  const persistGraph = useCallback(
    async (nextGraph: PrepJobGraph, name?: string) => {
      const currentId = jobId;
      if (currentId) {
        return prepService.update(currentId, { name: name ?? jobName, graph: nextGraph });
      }
      const created = await prepService.create({ name: (name ?? jobName) || "New Prep Job", graph: nextGraph });
      setJobId(created.id);
      loadedJobId.current = created.id;
      navigate(`/data-prep/${created.id}/design`, { replace: true });
      return created;
    },
    [jobId, jobName, navigate]
  );

  const syncFromFlow = useCallback(() => {
    const next = flowToGraph(nodes, edges, graphRef.current ?? undefined);
    graphRef.current = next;
    return next;
  }, [nodes, edges]);

  const registerDrawerDraftApplier = useCallback(
    (applier: ((graph: PrepJobGraph) => PrepJobGraph) | null) => {
      drawerDraftApplierRef.current = applier;
    },
    []
  );

  const getGraphForPersist = useCallback((): PrepJobGraph => {
    let graph = syncFromFlow();
    if (drawerDraftApplierRef.current) {
      graph = drawerDraftApplierRef.current(graph);
    }
    graphRef.current = graph;
    return graph;
  }, [syncFromFlow]);

  /** Server-side actions use the last saved design only — never persist implicitly. */
  const requireSavedJobId = useCallback((): string | null => {
    if (jobId) return jobId;
    setError(t("prep.design.saveFirst"));
    return null;
  }, [jobId, t]);

  const handleConnect = useCallback(
    (connection: Connection) => {
      const edgeId = crypto.randomUUID();
      const targetNode = nodes.find((n) => n.id === connection.target)?.data.graphNode;
      const needsFieldMapping = isPrepSourceType(targetNode?.type);
      const newEdge: Edge = {
        ...connection,
        id: edgeId,
        type: "prepCorrelation",
        label: needsFieldMapping ? "INNER" : "→",
        data: {
          graphEdge: {
            id: edgeId,
            sourceNodeId: connection.source!,
            targetNodeId: connection.target!,
            joinType: needsFieldMapping ? "INNER" : undefined,
          } satisfies PrepGraphEdge,
          incomplete: needsFieldMapping,
        },
      };
      setEdges((eds) => addEdge(newEdge, eds));
      setSelection({
        kind: "edge",
        edge: (newEdge.data as { graphEdge: PrepGraphEdge }).graphEdge,
        sourceNode: nodes.find((n) => n.id === connection.source)?.data.graphNode,
        targetNode: nodes.find((n) => n.id === connection.target)?.data.graphNode,
      });
      setDrawerOpen(true);
    },
    [nodes, setEdges]
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await persistGraph(getGraphForPersist(), jobName);
      setMessage(t("prep.design.saved"));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    const activeId = requireSavedJobId();
    if (!activeId) return;
    const result = await prepService.validate(activeId);
    const stale = result.staleNodeIds ?? [];
    setStaleNodeIds(stale);
    setValidationErrors(result.valid ? [] : result.errors);
    setLastValidatedAt(new Date().toISOString());
    setNodes((nds) => nds.map((n) => ({ ...n, data: { ...n.data, stale: stale.includes(n.id) } })));
    if (result.valid) {
      const warn = [...(result.warnings ?? []), ...(stale.length ? [t("prep.design.staleWarning")] : [])];
      setMessage(warn.length ? warn.join("; ") : t("prep.design.valid"));
      setError(null);
    } else {
      setError(result.errors.join("; "));
    }
  };

  const handleAutoLayout = () => {
    setNodes((nds) => autoLayoutNodes(nds, edges));
    setMessage(t("prep.design.autoLayoutDone"));
  };

  const addDslNode = useCallback(() => {
    const offset = nodes.filter((n) => n.type === "prepDslQuery").length;
    const node = newDslNode(80 + offset * 48, 80 + offset * 32);
    const flowNode: Node<PrepFlowNodeData> = {
      id: node.id,
      type: "prepDslQuery",
      position: { x: 80 + offset * 48, y: 80 + offset * 32 },
      deletable: true,
      data: { graphNode: node },
    };
    setNodes((nds) => [...nds, flowNode]);
    setSelection({ kind: "node", node });
    setDrawerOpen(true);
    setMessage(t("prep.design.dslAdded"));
    setValidationErrors([]);
  }, [nodes, setNodes, t]);

  const addAdaptorNode = useCallback(() => {
    const offset = nodes.filter((n) => n.type === "prepAdaptorQuery").length;
    const node = newAdaptorNode();
    const flowNode: Node<PrepFlowNodeData> = {
      id: node.id,
      type: "prepAdaptorQuery",
      position: { x: 80 + offset * 48, y: 180 + offset * 32 },
      deletable: true,
      data: { graphNode: node },
    };
    setNodes((nds) => [...nds, flowNode]);
    setSelection({ kind: "node", node });
    setDrawerOpen(true);
    setMessage(t("prep.design.adaptorAdded"));
    setValidationErrors([]);
  }, [nodes, setNodes, t]);

  const addOpenApiNode = useCallback(() => {
    const offset = nodes.filter((n) => n.type === "prepOpenApiQuery").length;
    const node = newOpenApiNode();
    const flowNode: Node<PrepFlowNodeData> = {
      id: node.id,
      type: "prepOpenApiQuery",
      position: { x: 80 + offset * 48, y: 280 + offset * 32 },
      deletable: true,
      data: { graphNode: node },
    };
    setNodes((nds) => [...nds, flowNode]);
    setSelection({ kind: "node", node });
    setDrawerOpen(true);
    setMessage(t("prep.design.openApiAdded"));
    setValidationErrors([]);
  }, [nodes, setNodes, t]);

  const handleTestNode = async (nodeId: string, _nodeSnapshot?: PrepGraphNode) => {
    const activeId = requireSavedJobId();
    if (!activeId) return;
    setTesting(true);
    try {
      // Runs against the last saved design only (no implicit persist of local drafts).
      await prepService.testNode(activeId, nodeId);
      const job = await prepService.get(activeId);
      const testedLast = job.graph.nodes.find((n) => n.id === nodeId)?.lastTest;
      const stale = (await prepService.validate(activeId)).staleNodeIds ?? [];
      setStaleNodeIds(stale);
      setNodes((nds) =>
        nds.map((n) => {
          const graphNode =
            n.id === nodeId && testedLast
              ? { ...n.data.graphNode, lastTest: testedLast }
              : n.data.graphNode;
          return {
            ...n,
            data: { ...n.data, graphNode, stale: stale.includes(n.id) },
          };
        })
      );
      setMessage(t("prep.design.nodeTested"));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(false);
    }
  };

  const handlePreviewChain = async () => {
    const activeId = requireSavedJobId();
    if (!activeId) return;
    const preview = await prepService.previewChain(activeId);
    if (preview.errors?.length) setError(preview.errors.join("; "));
    setChainPreview(preview);
    setChainPreviewOpen(true);
  };

  const handleRun = async () => {
    setError(null);
    try {
      const activeId = requireSavedJobId();
      if (!activeId) return;
      const graph = getGraphForPersist();
      if (!outputTargetIndex(graph)) {
        setError(t("prep.run.targetIndexRequired"));
        return;
      }
      const result = await runJob(activeId, true);
      if (result.started) setMessage(t("prep.design.runStarted"));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const handlePublish = async () => {
    setError(null);
    try {
      const activeId = requireSavedJobId();
      if (!activeId) return;
      if (analysisText.trim()) await prepService.updateAnalysis(activeId, analysisText);
      const result = await prepService.publish(activeId);
      setAnalysisText(result.job.analysisText ?? analysisText);
      setMessage(result.guideIngestTriggered ? t("prep.design.publishedWithGuide") : t("prep.design.published"));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleGenerateAnalysis = async () => {
    const activeId = requireSavedJobId();
    if (!activeId) return;
    const result = await prepService.generateAnalysis(activeId);
    setAnalysisText(result.analysisText ?? "");
    setAnalysisPanelOpen(true);
    setMessage(t("prep.design.analysisGenerated"));
  };

  const handleDeleteJob = async () => {
    if (!jobId) return;
    try {
      await confirmDelete({ itemName: jobName || jobId });
      await prepService.remove(jobId);
      navigate("/data-prep");
    } catch (e: unknown) {
      if (isCancelled(e)) return;
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleDeleteSelection = async () => {
    const itemName =
      selection?.kind === "node"
        ? selection.node.id
        : selection?.kind === "edge"
          ? selection.edge.id
          : undefined;
    try {
      await confirmDelete({ itemName });
    } catch (e: unknown) {
      if (isCancelled(e)) return;
      throw e;
    }
    if (selection?.kind === "node" && isPrepSourceType(selection.node.type)) {
      const nodeId = selection.node.id;
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setDrawerOpen(false);
      setSelection(null);
      setMessage(t("prep.drawer.nodeDeleted"));
    } else if (selection?.kind === "edge") {
      const edgeId = selection.edge.id;
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
      setDrawerOpen(false);
      setSelection(null);
      setMessage(t("prep.drawer.edgeDeleted"));
    }
  };

  const canDeleteSelection =
    selection?.kind === "edge" ||
    (selection?.kind === "node" && isPrepSourceType(selection.node.type));

  const handleSaveAnalysis = async () => {
    const activeId = requireSavedJobId();
    if (!activeId) return;
    await prepService.updateAnalysis(activeId, analysisText);
    setMessage(t("prep.design.analysisSaved"));
  };

  const onNodeClick = useCallback((_: unknown, node: Node<PrepFlowNodeData>) => {
    setSelection({ kind: "node", node: node.data.graphNode });
    setDrawerOpen(true);
  }, []);

  const onEdgeClick = useCallback(
    (_: unknown, edge: Edge) => {
      const graphEdge = (edge.data as { graphEdge?: PrepGraphEdge } | undefined)?.graphEdge;
      if (!graphEdge) return;
      setSelection({
        kind: "edge",
        edge: graphEdge,
        sourceNode: nodes.find((n) => n.id === edge.source)?.data.graphNode,
        targetNode: nodes.find((n) => n.id === edge.target)?.data.graphNode,
      });
      setDrawerOpen(true);
    },
    [nodes]
  );

  const handleSaveNode = (node: PrepGraphNode) => {
    setNodes((nds) => {
      const nextNodes = nds.map((n) =>
        n.id === node.id ? { ...n, data: { ...n.data, graphNode: node } } : n
      );
      graphRef.current = flowToGraph(nextNodes, edges, graphRef.current ?? undefined);
      return nextNodes;
    });
  };

  const handleSaveEdge = (edge: PrepGraphEdge) => {
    const targetNode = nodes.find((n) => n.id === edge.targetNodeId)?.data.graphNode;
    const isOutputEdge = targetNode?.type === "OUTPUT";
    const incomplete =
      !isOutputEdge &&
      isPrepSourceType(targetNode?.type) &&
      (!edge.upstreamField?.trim() || !edge.downstreamField?.trim());
    setEdges((eds) =>
      eds.map((e) =>
        e.id === edge.id
          ? {
              ...e,
              label: isOutputEdge ? "→" : (edge.joinType ?? "INNER"),
              data: { graphEdge: edge, incomplete },
            }
          : e
      )
    );
  };

  const currentGraph = useMemo(
    () => flowToGraph(nodes, edges, graphRef.current ?? undefined),
    [nodes, edges]
  );
  const incompleteEdgeCount = useMemo(() => countIncompleteEdges(currentGraph), [currentGraph]);

  useEffect(() => {
    if (selection?.kind === "node" && !nodes.some((n) => n.id === selection.node.id)) {
      setDrawerOpen(false);
      setSelection(null);
    }
    if (selection?.kind === "edge" && !edges.some((e) => e.id === selection.edge.id)) {
      setDrawerOpen(false);
      setSelection(null);
    }
  }, [nodes, edges, selection]);

  return (
    <Box>
      <PageCommandBar
        title={t("prep.design.title")}
        subtitle={jobId ?? t("prep.design.new")}
      />
      <Stack spacing={2} mt={2} mb={2}>
        <Paper elevation={0} sx={{ p: 2 }}>
          <AppTextField
            label={t("prep.design.jobName")}
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            fullWidth
          />
        </Paper>
        <PrepDesignToolbar
          saving={saving}
          running={running}
          jobId={jobId}
          onAddDsl={addDslNode}
          onAddAdaptor={addAdaptorNode}
          onAddOpenApi={addOpenApiNode}
          onAutoLayout={handleAutoLayout}
          onValidate={handleValidate}
          onPreviewChain={handlePreviewChain}
          onSave={handleSave}
          onPublish={handlePublish}
          onRun={handleRun}
          onViewRuns={() => jobId && navigate(`/data-prep/${jobId}/runs`)}
          onOpenAnalysis={() => setAnalysisPanelOpen(true)}
          onGenerateAnalysis={handleGenerateAnalysis}
          onDeleteJob={handleDeleteJob}
        />
        {error && <Alert severity="error">{error}</Alert>}
        {message && (
          <Alert severity="success" onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}
        {staleNodeIds.length > 0 && (
          <Alert severity="warning">
            {t("prep.design.staleWarning")}: {staleNodeIds.join(", ")}
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary">
          {t("prep.design.canvasHint")}
        </Typography>
      </Stack>

      <PrepFlowCanvas
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={handleConnect}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        nodeCount={nodes.length}
        edgeCount={edges.length}
        staleCount={staleNodeIds.length}
        incompleteEdgeCount={incompleteEdgeCount}
        validationErrors={validationErrors}
        lastValidatedAt={lastValidatedAt}
      />

      <PrepAnalysisPanel
        open={analysisPanelOpen}
        analysisText={analysisText}
        onChange={setAnalysisText}
        onClose={() => setAnalysisPanelOpen(false)}
        onSave={handleSaveAnalysis}
        saving={saving}
      />

      <PrepConfigDrawer
        open={drawerOpen}
        selection={selection}
        graph={currentGraph}
        jobId={jobId}
        onClose={() => setDrawerOpen(false)}
        onSaveNode={handleSaveNode}
        onSaveEdge={handleSaveEdge}
        onDeleteSelection={handleDeleteSelection}
        canDeleteSelection={canDeleteSelection}
        onTestNode={jobId ? handleTestNode : undefined}
        onViewRuns={jobId ? () => navigate(`/data-prep/${jobId}/runs`) : undefined}
        testing={testing}
        onRegisterDraftApplier={registerDrawerDraftApplier}
      />
      <PrepChainPreviewDrawer
        open={chainPreviewOpen}
        preview={chainPreview}
        onClose={() => setChainPreviewOpen(false)}
      />
      <PrepExpansionRunDialog
        open={expansionDialogOpen}
        estimate={expansionEstimate}
        onCancel={cancelExpansionRun}
        onConfirm={() => confirmExpansionRun(true)}
      />
    </Box>
  );
}
